/* ==========================================================================
   Shoplift Supply — facets.js
   Progressive enhancement for main-collection.liquid's filter/sort forms.

   The markup is a real GET <form action="{{ collection.url }}"> with Shopify's
   native filter param names (value.param_name/value.value, filter.v.price.gte|lte,
   sort_by) — the page filters correctly with JavaScript disabled.

   With JS: serialize the form that changed -> strip empty price + page params ->
   history.pushState the resulting URL -> fetch the same URL + &section_id=<this
   section> -> patch just the result nodes (item counts, both product grids,
   pagination, empty state) by id, leaving every open dropdown/drawer exactly as
   the shopper left it. popstate re-fetches the same way without pushing state.

   Custom elements:
     <facet-form>    wraps the desktop pill-bar form and the mobile drawer form.
     <filter-drawer> the mobile bottom sheet; extends Theme.DialogComponent.
   ========================================================================== */

(function () {
  'use strict';

  var Theme = window.Theme || (window.Theme = {});

  /* ------------------------------------------------------------------------
     Shared helpers
     ------------------------------------------------------------------------ */

  /**
   * Drops a price bound that is still sitting on the control's own extreme.
   * The drawer's two <input type="range"> elements always carry a value, so
   * without this an untouched price slider would submit the full range as a
   * real filter — and any rounding in that ceiling would hide products.
   * @param {HTMLFormElement} form
   * @param {URLSearchParams} params
   */
  function stripUntouchedPriceBounds(form, params) {
    var pairs = [
      [form.querySelector('[data-price-min]'), 'min'],
      [form.querySelector('[data-price-max]'), 'max'],
    ];

    pairs.forEach(function (pair) {
      var input = pair[0];
      if (!input || !input.name) return;

      var bound = parseFloat(input.getAttribute(pair[1]));
      var value = parseFloat(input.value);
      if (isNaN(bound) || isNaN(value)) return;

      var untouched = pair[1] === 'min' ? value <= bound : value >= bound;
      if (untouched) params.delete(input.name);
    });
  }

  /**
   * Builds the URL that should be requested/pushed for a given form's current
   * state — strips empty/untouched price bounds and the page param per the
   * facets spec.
   * @param {HTMLFormElement} form
   * @returns {string}
   */
  function buildFacetUrl(form) {
    var params = new URLSearchParams(new FormData(form));

    ['filter.v.price.gte', 'filter.v.price.lte'].forEach(function (key) {
      if (params.get(key) === '') params.delete(key);
    });
    stripUntouchedPriceBounds(form, params);
    params.delete('page');

    var action = form.getAttribute('action') || window.location.pathname;
    var qs = params.toString();
    return qs ? action + '?' + qs : action;
  }

  /**
   * Replaces every element in `targetIds` with its counterpart parsed from
   * `html`, matched by id. Missing ids on either side are skipped silently
   * (pagination/empty-state nodes don't always exist).
   * @param {string} html
   * @param {string[]} targetIds
   */
  function patchResultNodes(html, targetIds) {
    var doc = new DOMParser().parseFromString(html, 'text/html');

    targetIds.forEach(function (id) {
      var current = document.getElementById(id);
      var next = doc.getElementById(id);
      if (current && next) {
        current.replaceWith(next);
      } else if (current && !next) {
        // e.g. pagination disappeared because the new result set fits one page.
        current.remove();
      }
    });
  }

  /**
   * @param {string} sectionId
   * @returns {string[]} the ids patchResultNodes should look for.
   */
  function resultNodeIds(sectionId) {
    return [
      'CollectionCount-' + sectionId,
      'CollectionCountMobile-' + sectionId,
      'CollectionGridDesktop-' + sectionId,
      'CollectionGridMobile-' + sectionId,
      'CollectionEmpty-' + sectionId,
      'CollectionPagination-' + sectionId,
      'FilterDrawerShowBtn-' + sectionId,
    ];
  }

  /**
   * Latest-request token per section. Toggling several checkboxes (or tabbing
   * through both price fields) fires overlapping fetches; without this the
   * slowest response would win and leave the grid showing a filter combination
   * that no longer matches the inputs.
   * @type {Record<string, number>}
   */
  var requestTokens = {};

  /** @type {Record<string, AbortController>} */
  var requestControllers = {};

  /**
   * Fetches an isolated render of `sectionId` at `url` and patches the result
   * nodes in place.
   *
   * Pagination links and `window.location.href` carry a `#CollectionResultsTop-…`
   * fragment, so `section_id` has to be appended to the path+query — appending it
   * to the whole string would bury it inside the fragment, where the server never
   * sees it and the response is the entire page instead of the section.
   *
   * @param {string} url
   * @param {string} sectionId
   * @param {{push?: boolean}} [options]
   */
  function applyUrl(url, sectionId, options) {
    var opts = options || {};

    var hashIndex = url.indexOf('#');
    var base = hashIndex === -1 ? url : url.slice(0, hashIndex);
    var sep = base.indexOf('?') === -1 ? '?' : '&';
    var fetchUrl = base + sep + 'section_id=' + encodeURIComponent(sectionId);

    var token = (requestTokens[sectionId] || 0) + 1;
    requestTokens[sectionId] = token;

    if (requestControllers[sectionId]) requestControllers[sectionId].abort();
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    if (controller) requestControllers[sectionId] = controller;

    return fetch(fetchUrl, {
      headers: { Accept: 'text/html' },
      signal: controller ? controller.signal : undefined,
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Facet request failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        // A newer request started while this one was in flight — discard it.
        if (requestTokens[sectionId] !== token) return;
        if (requestControllers[sectionId] === controller) delete requestControllers[sectionId];

        patchResultNodes(html, resultNodeIds(sectionId));
        if (opts.push !== false) {
          history.pushState({ url: url }, '', url);
        }
        Theme.publish('facets:updated', { url: url, sectionId: sectionId });
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        if (requestTokens[sectionId] !== token) return;
        // Network hiccup: fall back to a real navigation so filtering still works.
        window.location.assign(url);
        if (window.console) console.error(error);
      });
  }

  /* ------------------------------------------------------------------------
     <facet-form> — wraps a single <form>. Applying is shared by the desktop
     pill bar and the mobile drawer; both forms independently carry the full
     current filter + sort state, so whichever one changed is self-sufficient.
     ------------------------------------------------------------------------ */

  class FacetFormElement extends HTMLElement {
    connectedCallback() {
      if (this.dataset.facetFormReady === 'true') return;
      this.dataset.facetFormReady = 'true';

      this.form = this.querySelector('form[data-facet-form]');
      if (!this.form) return;

      this.sectionId = this.form.getAttribute('data-section-id');

      // Marks this wrapper so CSS can hide the no-JS fallback submit button —
      // once this runs we auto-apply on every change instead.
      this.classList.add('facet-form--js');

      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);

      this.form.addEventListener('change', this.onChange);
      this.form.addEventListener('submit', this.onSubmit);
    }

    onChange(event) {
      var target = event.target;
      // Range inputs fire `change` on release (not while dragging) — that's the
      // point where we want to submit. Text price inputs fire on blur/Enter.
      if (target && target.matches('input[type="range"]')) {
        this.syncPriceSlider(target);
      }
      this.apply();
    }

    onSubmit(event) {
      event.preventDefault();
      this.apply();
    }

    apply() {
      var url = buildFacetUrl(this.form);
      applyUrl(url, this.sectionId).then(this.afterApply.bind(this));
    }

    /** Closes the parent drawer once the fetch resolves, if this form lives in one. */
    afterApply() {
      var drawer = this.closest('filter-drawer');
      if (drawer && drawer.dataset.closeOnApply === 'true' && typeof drawer.close === 'function') {
        drawer.close();
      }
    }

    /** Keeps the two range thumbs from crossing and updates the live label. */
    syncPriceSlider(input) {
      var wrapper = input.closest('[data-price-slider]');
      if (!wrapper) return;

      var minInput = wrapper.querySelector('[data-price-min]');
      var maxInput = wrapper.querySelector('[data-price-max]');
      if (!minInput || !maxInput) return;

      var min = parseFloat(minInput.value || minInput.min || 0);
      var max = parseFloat(maxInput.value || maxInput.max || 0);

      if (min > max) {
        if (input === minInput) {
          minInput.value = max;
        } else {
          maxInput.value = min;
        }
      }

      updatePriceSliderVisuals(wrapper, minInput, maxInput);
    }
  }

  /**
   * Repaints a price slider's fill bar + live label from its current input
   * values. Exported for the initial paint on connect.
   * @param {HTMLElement} wrapper
   * @param {HTMLInputElement} minInput
   * @param {HTMLInputElement} maxInput
   */
  function updatePriceSliderVisuals(wrapper, minInput, maxInput) {
    var lo = parseFloat(minInput.min || 0);
    var hi = parseFloat(maxInput.max || 0);
    var range = hi - lo || 1;

    var minPct = ((parseFloat(minInput.value || lo) - lo) / range) * 100;
    var maxPct = ((parseFloat(maxInput.value || hi) - lo) / range) * 100;

    var fill = wrapper.querySelector('[data-price-fill]');
    if (fill) {
      fill.style.left = minPct + '%';
      fill.style.right = 100 - maxPct + '%';
    }

    var label = wrapper.querySelector('[data-price-label]');
    if (label) {
      var currency = wrapper.getAttribute('data-currency-symbol') || '$';
      label.textContent =
        currency + Math.round(parseFloat(minInput.value || lo)) + ' – ' + currency + Math.round(parseFloat(maxInput.value || hi));
    }
  }

  if (!customElements.get('facet-form')) {
    customElements.define('facet-form', FacetFormElement);
  }

  /* ------------------------------------------------------------------------
     <filter-drawer> — mobile bottom sheet. Extends Theme.DialogComponent for
     open/close/focus-trap/ESC/overlay-click; adds the "scroll to a section"
     behaviour so the Filter pill and the Sort pill can open the same sheet at
     different scroll positions, and marks itself close-on-apply for its form.
     ------------------------------------------------------------------------ */

  function initFilterDrawer() {
    if (!Theme.DialogComponent || customElements.get('filter-drawer')) return;

    class FilterDrawer extends Theme.DialogComponent {
      connectedCallback() {
        super.connectedCallback();
        this.dataset.closeOnApply = 'true';
      }

      /** @param {HTMLElement} opener @param {string} [scrollToId] */
      openAt(opener, scrollToId) {
        this.open(opener);
        if (!scrollToId) return;
        var target = this.querySelector('#' + scrollToId);
        if (target) {
          window.requestAnimationFrame(function () {
            target.scrollIntoView({ block: 'start', behavior: 'auto' });
          });
        }
      }
    }

    customElements.define('filter-drawer', FilterDrawer);
  }

  /* ------------------------------------------------------------------------
     Openers: any [data-open-filter-drawer] button opens its target drawer,
     optionally scrolled to [data-scroll-to].
     ------------------------------------------------------------------------ */

  function bindDrawerOpeners() {
    document.querySelectorAll('[data-open-filter-drawer]').forEach(function (button) {
      if (button.dataset.filterDrawerBound === 'true') return;
      button.dataset.filterDrawerBound = 'true';

      button.addEventListener('click', function () {
        var drawer = document.getElementById(button.getAttribute('data-open-filter-drawer'));
        if (!drawer) return;
        drawer.openAt(button, button.getAttribute('data-scroll-to'));
      });
    });
  }

  /* ------------------------------------------------------------------------
     "Clear all" — the mobile drawer renders it in the sheet header, above and
     outside <facet-form>, so it is bound by delegation off its data-section-id
     rather than from inside the custom element.
     ------------------------------------------------------------------------ */

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    if (!(event.target instanceof Element)) return;

    var clear = event.target.closest('[data-facet-clear-all]');
    if (!clear) return;

    var sectionId = clear.getAttribute('data-section-id');
    if (!sectionId) return;

    event.preventDefault();
    clearFacetControls(sectionId);
    applyUrl(clear.getAttribute('href') || window.location.pathname, sectionId).then(function () {
      var drawer = clear.closest('filter-drawer');
      if (drawer && typeof drawer.close === 'function') drawer.close();
    });
  });

  /**
   * Unchecks every facet control and returns the price inputs to their extremes
   * for a section's forms. The section re-render deliberately leaves the filter
   * forms alone, so "Clear all" has to clear them itself.
   * @param {string} sectionId
   */
  function clearFacetControls(sectionId) {
    var forms = document.querySelectorAll(
      'form[data-facet-form][data-section-id="' + sectionId + '"]'
    );

    Array.prototype.forEach.call(forms, function (form) {
      Array.prototype.forEach.call(form.querySelectorAll('input[type="checkbox"]'), function (input) {
        input.checked = false;
      });
      Array.prototype.forEach.call(form.querySelectorAll('input[type="number"]'), function (input) {
        input.value = '';
      });

      var minInput = form.querySelector('[data-price-min]');
      var maxInput = form.querySelector('[data-price-max]');
      if (!minInput || !maxInput) return;

      minInput.value = minInput.getAttribute('min') || '0';
      maxInput.value = maxInput.getAttribute('max') || '0';

      var wrapper = form.querySelector('[data-price-slider]');
      if (wrapper) updatePriceSliderVisuals(wrapper, minInput, maxInput);
    });
  }

  /* ------------------------------------------------------------------------
     Pagination inside the results area is enhanced the same way as facets —
     a plain link that we intercept to fetch + patch + pushState.
     ------------------------------------------------------------------------ */

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    if (!(event.target instanceof Element)) return;

    var link = event.target.closest('[id^="CollectionPagination-"] a.pagination__item');
    if (!link) return;

    var wrapper = link.closest('[id^="CollectionPagination-"]');
    var sectionId = wrapper ? wrapper.id.replace('CollectionPagination-', '') : null;
    if (!sectionId) return;

    event.preventDefault();
    applyUrl(link.href, sectionId).then(function () {
      var top = document.getElementById('CollectionResultsTop-' + sectionId);
      if (top) top.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  });

  /* ------------------------------------------------------------------------
     popstate — re-fetch whichever collection section is on the page without
     pushing a new history entry.
     ------------------------------------------------------------------------ */

  window.addEventListener('popstate', function () {
    document.querySelectorAll('[data-section-type="main-collection"]').forEach(function (root) {
      var sectionId = root.getAttribute('data-section-id');
      if (sectionId) applyUrl(window.location.href, sectionId, { push: false });
    });
  });

  /* ------------------------------------------------------------------------
     Init
     ------------------------------------------------------------------------ */

  function init() {
    initFilterDrawer();
    bindDrawerOpeners();

    document.querySelectorAll('[data-price-slider]').forEach(function (wrapper) {
      var minInput = wrapper.querySelector('[data-price-min]');
      var maxInput = wrapper.querySelector('[data-price-max]');
      if (minInput && maxInput) updatePriceSliderVisuals(wrapper, minInput, maxInput);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
